import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Truck } from 'lucide-react';
import { toast } from 'sonner';

import { useAuth } from '../../../context/AuthContext';
import { supplyApi } from '../../../lib/supplyApi';
import { messageErreur } from '../../../lib/erreurs';
import { libelle } from '../../../lib/libelles';
import { formatLitres, formatNombre, formatPourcent } from '../../../lib/formats';
import { formatCurrency } from '../../../lib/utils';
import { Aide } from '../../ui/aide';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';

/**
 * Fiche voyage (lot 032) : étape « Réception en station » et chaîne
 * chargé → arrivé → reçu en cuve → vendu aux pompes, maquette « Voyage vers ma
 * station ». Les chiffres viennent du serveur (`/station-supply/trips/{id}/chain`) ;
 * rien ne s'affiche pour un voyage qui ne livre aucune station.
 */

const vide = (valeur) => valeur === null || valeur === undefined || valeur === '';

/** Écart signé en litres : « −15 L », « +3 L », « 0 L ». */
const ecart = (valeur) => {
  if (vide(valeur)) return null;
  const n = Number(valeur);
  const signe = n > 0 ? '+' : n < 0 ? '−' : '';
  return `${signe}${formatNombre(Math.abs(n), 0)} L`;
};

const pourcent = (valeur) => {
  if (vide(valeur)) return null;
  const n = Number(valeur);
  const signe = n < 0 ? '−' : '';
  return `${signe}${formatPourcent(Math.abs(n), 2)}`;
};

const TONS = {
  ok: 'border-success/25 bg-success/10 text-success',
  attention: 'border-flame/30 bg-flame/10 text-flame-ink',
  neutre: 'border-border bg-muted text-muted-foreground',
};

const Maillon = ({ ou, litres, detail, ton = 'neutre', testId }) => (
  <div className="flex min-w-0 flex-col gap-1 rounded-lg border border-border bg-background px-3 py-2" data-testid={testId}>
    <span className="text-xs font-medium text-muted-foreground">{ou}</span>
    <span className="text-lg font-semibold tabular-nums text-foreground">{vide(litres) ? '—' : formatLitres(litres)}</span>
    {detail ? (
      <span className={`w-fit rounded-md border px-1.5 py-0.5 text-xs font-medium ${TONS[ton] || TONS.neutre}`}>{detail}</span>
    ) : null}
  </div>
);

const LigneMontant = ({ label, valeur, total = false, positif = false }) => (
  <tr className={total ? 'border-t border-border font-semibold' : undefined}>
    <td className="py-1.5 pr-3 text-sm">{label}</td>
    <td className={`py-1.5 text-right text-sm tabular-nums ${positif ? 'text-success' : ''}`}>
      {vide(valeur) ? '—' : formatNombre(valeur, 0)}
    </td>
  </tr>
);

const PASTILLE_FACTURE = {
  NONE: 'Pas de facture client',
  INTERNAL: 'Facture interne au prix de revient',
  CLIENT: 'Vente au client',
};

export const TripStationChain = ({ tripId, onChanged }) => {
  const { canSeeMoney, canWriteModule } = useAuth();
  const [chaine, setChaine] = useState(null);
  const [occupe, setOccupe] = useState(false);
  const peutPreparer = ['trips', 'tanks', 'purchases'].some((code) => canWriteModule?.(code));

  const charger = useCallback(async () => {
    if (!tripId) return;
    try {
      setChaine(await supplyApi.chain(tripId));
    } catch {
      // Sans le lien (droit absent, hors ligne), la fiche voyage reste entière.
      setChaine(null);
    }
  }, [tripId]);

  useEffect(() => {
    charger();
  }, [charger]);

  if (!chaine?.linked) return null;

  const preparer = async () => {
    setOccupe(true);
    try {
      await supplyApi.prepare(tripId);
      toast.success('Réception préparée en station');
      await charger();
      onChanged?.();
    } catch (erreur) {
      toast.error(messageErreur(erreur, 'Impossible de préparer la réception. Réessayez.'));
    } finally {
      setOccupe(false);
    }
  };

  const maillons = chaine.links || {};
  const transportTon =
    maillons.transport_classification === 'OUTSIDE_TOLERANCE'
      ? 'attention'
      : maillons.transport_classification === 'UNKNOWN'
        ? 'neutre'
        : 'ok';
  const receptionTon = Math.abs(Number(maillons.reception_variance_percent || 0)) > 0.5 ? 'attention' : 'ok';
  const revient = chaine.landed || {};
  const resultat = chaine.result || {};
  const montants = canSeeMoney && !vide(revient.landed_cost);
  const stations = (chaine.stations || []).map((station) => station.name).filter(Boolean).join(', ');

  return (
    <section className="space-y-3 rounded-xl border border-border bg-card px-4 py-3" data-testid="voyage-station">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1.5">
          <Truck className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <h2 className="text-base font-semibold text-foreground">Réception en station</h2>
          {stations ? <span className="truncate text-sm text-muted-foreground">{stations}</span> : null}
          <Aide titre="Voyage vers ma station">
            Chaque maillon a son écart : le manquant de transport (arrivée du camion moins chargement, tolérance de 100 L
            par voyage) et l’écart de réception (reçu en cuve moins arrivée). Le prix de revient additionne le coût du
            produit au dépôt, le transport par le camion et les litres perdus répartis sur les litres reçus.
          </Aide>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={chaine.supply_kind === 'INTERNAL_SUPPLY' ? 'success' : 'muted'}>
            {libelle('commande_nature', chaine.supply_kind)}
          </Badge>
          <Badge variant="muted">{PASTILLE_FACTURE[chaine.invoice_kind] || PASTILLE_FACTURE.CLIENT}</Badge>
          {chaine.partial ? <Badge variant="warning">Réception partielle</Badge> : null}
          {chaine.can_prepare && peutPreparer ? (
            <Button size="sm" onClick={preparer} disabled={occupe} data-testid="preparer-reception">
              Préparer la réception
            </Button>
          ) : null}
        </div>
      </header>

      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4" data-testid="chaine-voyage">
        <Maillon
          ou="Chargé au dépôt"
          litres={maillons.loaded_liters}
          detail={maillons.loaded_basis === '15C' ? 'à 15 °C' : vide(maillons.loaded_liters) ? null : 'sans température'}
          testId="maillon-charge"
        />
        <Maillon
          ou="Arrivé à la station"
          litres={maillons.arrived_liters}
          detail={
            vide(maillons.transport_variance_liters)
              ? maillons.arrived_basis === 'AMBIENT' && !vide(maillons.arrived_liters)
                ? 'sans température'
                : null
              : `${ecart(maillons.transport_variance_liters)}, tolérance ${formatNombre(maillons.transport_tolerance_liters, 0)} L`
          }
          ton={transportTon}
          testId="maillon-arrive"
        />
        <Maillon
          ou="Reçu en cuve"
          litres={maillons.received_liters}
          detail={
            vide(maillons.reception_variance_liters)
              ? null
              : [ecart(maillons.reception_variance_liters), pourcent(maillons.reception_variance_percent)].filter(Boolean).join(', ')
          }
          ton={receptionTon}
          testId="maillon-recu"
        />
        <Maillon
          ou="Vendu aux pompes depuis"
          litres={maillons.sold_liters}
          detail={vide(maillons.remaining_liters) ? null : `reste ${formatLitres(maillons.remaining_liters)} en cuve`}
          testId="maillon-vendu"
        />
      </div>

      {chaine.receptions?.length ? (
        <ul className="divide-y divide-border/60 rounded-lg border border-border" data-testid="receptions-voyage">
          {chaine.receptions.map((reception) => (
            <li key={reception.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
              <span className="min-w-0">
                <span className="font-medium text-foreground">{reception.tank_name || 'Cuve'}</span>
                <span className="text-muted-foreground">
                  {' · '}
                  {[reception.product_name, reception.station_name, reception.delivery_note_number].filter(Boolean).join(' · ')}
                </span>
              </span>
              <span className="flex items-center gap-2">
                <span className="tabular-nums text-muted-foreground">{formatLitres(reception.loaded_liters)}</span>
                <Badge variant={reception.status === 'VALIDATED' ? 'success' : reception.status === 'CANCELLED' ? 'muted' : 'warning'}>
                  {libelle('station_reception', reception.status)}
                </Badge>
                <Button asChild size="sm" variant="ghost">
                  <Link to={reception.reception_path || `/stations/receptions/${reception.id}`}>Ouvrir</Link>
                </Button>
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {montants ? (
        <div className="grid gap-3 md:grid-cols-2">
          <div className="rounded-lg border border-border px-3 py-2" data-testid="prix-de-revient">
            <h3 className="flex items-baseline justify-between text-sm font-semibold">
              Prix de revient en cuve <span className="text-xs font-normal text-muted-foreground">FCFA par litre</span>
            </h3>
            <table className="w-full">
              <tbody>
                <LigneMontant label="Produit au dépôt, coût moyen" valeur={revient.product_cost} />
                <LigneMontant label="Transport par le camion" valeur={revient.freight_per_liter} />
                <LigneMontant label="Manquants répartis" valeur={revient.shortage_per_liter} />
                <LigneMontant label="Prix de revient" valeur={revient.landed_cost} total />
              </tbody>
            </table>
          </div>
          <div className="rounded-lg border border-border px-3 py-2" data-testid="resultat-chargement">
            <h3 className="flex items-baseline justify-between text-sm font-semibold">
              Résultat sur ce chargement <span className="text-xs font-normal text-muted-foreground">FCFA</span>
            </h3>
            <table className="w-full">
              <tbody>
                <LigneMontant label="Prix de vente saisi" valeur={resultat.pump_price} />
                <LigneMontant label="Marge par litre" valeur={resultat.margin_per_liter} positif={Number(resultat.margin_per_liter) > 0} />
                <tr>
                  <td className="py-1.5 pr-3 text-sm">Recette du camion pour ce voyage</td>
                  <td className="py-1.5 text-right text-sm tabular-nums">{vide(resultat.truck_revenue) ? '—' : formatCurrency(resultat.truck_revenue)}</td>
                </tr>
                <tr>
                  <td className="py-1.5 pr-3 text-sm">
                    Marge déjà réalisée{vide(maillons.sold_liters) ? '' : `, ${formatLitres(maillons.sold_liters)}`}
                  </td>
                  <td className={`py-1.5 text-right text-sm tabular-nums ${Number(resultat.realized_margin) > 0 ? 'text-success' : ''}`}>
                    {vide(resultat.realized_margin) ? '—' : formatCurrency(resultat.realized_margin)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default TripStationChain;
